import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import actions from "actions";
import { useGlobalToast } from "components/GlobalToastProvider";

interface UseUpdateAuthorizationParams {
  clientId: string;
  authorizationCodes: any[];
  availableInsuranceCodes: any[];
  overlapError: boolean;
  onOverlapDetected: () => void;
  checkForOverlaps: (params: any) => void;
  resetOverlap: () => void;
}

const useUpdateAuthorization = ({
  clientId,
  authorizationCodes,
  availableInsuranceCodes,
  overlapError,
  onOverlapDetected,
  checkForOverlaps,
}: UseUpdateAuthorizationParams) => {
  const dispatch = useDispatch();
  const { showToast } = useGlobalToast();

  const [open, setOpen] = useState(false);
  const [id, setId] = useState<number | null>(null);
  const [code, setCode] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [authType, setAuthType] = useState("");
  const [clinicianId, setClinicianId] = useState("");
  const [pendingAuth, setPendingAuth] = useState(false);
  const [approvedUnits, setApprovedUnits] = useState([
    { code: "", units: 0, hours: 0, codeId: "", unitId: 0 },
  ]);
  const [newApprovedUnits, setNewApprovedUnits] = useState<any[]>([]);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

  useEffect(() => {
    if (open) {
      checkForOverlaps({
        startDate,
        endDate,
        authType,
        approvedUnits: [...approvedUnits, ...newApprovedUnits],
        excludeId: id,
      });
    }
  }, [startDate, endDate, authType, approvedUnits, newApprovedUnits, open, checkForOverlaps, id]);

  const openDialog = (index: number) => {
    setOpen(true);
    const selected = authorizationCodes[index];
    setId(selected.id);
    setCode(selected.authorization_code);
    setStartDate(new Date(selected.start_date));
    setEndDate(new Date(selected.end_date));
    setAuthType(selected.authorization_type || "");
    setClinicianId(selected.clinician_id || "");
    setPendingAuth(selected.pending_auth);
    setApprovedUnits(
      selected.approvedUnits.map(
        ({
          approved_time: hours,
          approved_units: units,
          code: unitCode,
          id: unitId,
          insurance_code_id: codeId,
          client_id: clientIdVal,
        }: any) => ({
          hours,
          units,
          code: unitCode,
          unitId,
          codeId: parseInt(codeId),
          clientId: clientIdVal,
        })
      )
    );
    setSaveButtonDisabled(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setCode("");
    setStartDate(null);
    setEndDate(null);
    setAuthType("");
    setClinicianId("");
    setPendingAuth(false);
    setApprovedUnits([{ code: "", units: 0, hours: 0, codeId: "", unitId: 0 }]);
    setSaveButtonDisabled(true);
    setNewApprovedUnits([]);
  };

  const handleChange = (name: string) => (e: any) => {
    const { checked, type, value } = e.target;
    const val = type === "checkbox" ? checked : value;
    setSaveButtonDisabled(false);
    switch (name) {
      case "updateAuthorizationCode": setCode(val); break;
      case "updateAuthorizationType": setAuthType(val); break;
      case "updateAuthorizationClinicianId": setClinicianId(val); break;
      case "updatePendingAuth": setPendingAuth(val); break;
    }
  };

  const handleDateChange = (name: string, date: Date | null) => {
    setSaveButtonDisabled(false);
    switch (name) {
      case "updateAuthorizationStartDate": setStartDate(date); break;
      case "updateAuthorizationEndDate": setEndDate(date); break;
    }
  };

  const changeApprovedUnitAmount = (index: number, e: any) => {
    const units = [...approvedUnits];
    units[index].units = e.target.value;
    units[index].hours = e.target.value / 4;
    setApprovedUnits(units);
    setSaveButtonDisabled(false);
  };

  const addNewApprovedUnit = () => {
    setNewApprovedUnits([
      ...newApprovedUnits,
      { code: "", units: 0, hours: 0, codeId: 0 },
    ]);
  };

  const removeNewApprovedUnit = (index: number) => {
    const units = [...newApprovedUnits];
    units.splice(index, 1);
    setNewApprovedUnits(units);
  };

  const changeNewApprovedUnitCode = (index: number, e: any) => {
    const units = [...newApprovedUnits];
    units[index].code = e.target.value;
    const codeId = availableInsuranceCodes.find(
      (c: any) => c.insurance_code === e.target.value
    )?.id;
    units[index].codeId = codeId;
    setNewApprovedUnits(units);
    setSaveButtonDisabled(false);
  };

  const changeNewApprovedUnitAmount = (index: number, e: any) => {
    const units = [...newApprovedUnits];
    units[index].units = e.target.value;
    units[index].hours = e.target.value / 4;
    setNewApprovedUnits(units);
    setSaveButtonDisabled(false);
  };

  const save = () => {
    if (overlapError) {
      onOverlapDetected();
    } else {
      submit();
    }
  };

  const submit = (isSecondaryAuth?: boolean) => {
    const secondaryAuth =
      isSecondaryAuth !== undefined ? isSecondaryAuth : authType === "secondary_auth";

    dispatch(
      actions.updateAuthorizationCode({
        isSecondaryAuth: secondaryAuth,
        authId: id,
        authorizationCode: code,
        startDate,
        endDate,
        clientId: parseInt(clientId),
        approvedUnits: secondaryAuth ? null : approvedUnits,
        authorizationType: authType,
        clinicianId,
        pendingAuth,
      })
    );

    if (newApprovedUnits.length > 0) {
      for (let i = 0; i < newApprovedUnits.length; i++) {
        const unit = newApprovedUnits[i];
        unit.insuranceAuthorizationsId = id;
        unit.clientId = parseInt(clientId);
        dispatch(actions.createApprovedUnits(unit));
      }
    }

    setId(null);
    setCode("");
    setEndDate(null);
    setStartDate(null);
    setOpen(false);
    setAuthType("");
    setClinicianId("");
    setPendingAuth(false);
    setApprovedUnits([{ code: "", units: 0, hours: 0, codeId: "", unitId: 0 }]);
    setNewApprovedUnits([]);

    dispatch(actions.getAuthorizationCodes(parseInt(clientId)));
    showToast({ message: "Authorization updated successfully" });
  };

  return {
    open,
    id,
    code,
    startDate,
    endDate,
    authType,
    clinicianId,
    pendingAuth,
    approvedUnits,
    newApprovedUnits,
    saveButtonDisabled,
    openDialog,
    closeDialog,
    handleChange,
    handleDateChange,
    changeApprovedUnitAmount,
    addNewApprovedUnit,
    removeNewApprovedUnit,
    changeNewApprovedUnitCode,
    changeNewApprovedUnitAmount,
    save,
    submit,
  };
};

export default useUpdateAuthorization;
